import { Component } from "react"
import PropTypes from "prop-types"
import classnames from "classnames"

import { post } from "shared/lib/api"
import { rejectedXHRPromiseLogger } from "shared/lib/xhr_promise"
import HoverTrigger from "shared/components/hover_trigger"
import Spinner from "shared/components/spinner"

import styles from "./admin_panel.module.scss"

const REVIEW_STATUS_IN_DEVELOPMENT = 0
const REVIEW_STATUS_PASSED_REVIEW = 2

export default class AdminPanel extends Component {
  state = {
    reviewStatus: this.props.reviewStatus,
    published: this.props.published,
    updating: false,
    verifyIntent: false,
    rejectSelected: false,
  }

  mustVerifyIntent = (rejectSelected = false) => {
    this.setState({ verifyIntent: true, rejectSelected })
  }

  intentWasVerified = () => {
    this.setState({ verifyIntent: false })
  }

  advanceReviewProgress = () => {
    this.updateReviewStatus(
      this.props.paths[
        this.mapStatusToAction(this.state.reviewStatus + 1).action
      ]
    )
  }

  returnToDevelopment = () => {
    this.updateReviewStatus(
      this.props.paths[
        this.mapStatusToAction(REVIEW_STATUS_IN_DEVELOPMENT).action
      ]
    )
  }

  updateReviewStatus = url => {
    if (this.state.updating) return
    this.setState({ updating: true })

    post(url, {
      service_slug: this.props.service.slug,
      organization_id: this.props.service.organization.id,
    })
      .then(data => {
        this.setState({
          reviewStatus: data.review_status,
          published: data.published,
          updating: false,
          verifyIntent: false,
        })
      })
      .catch(
        rejectedXHRPromiseLogger(data => {
          this.setState({ updating: false, verifyIntent: false })
          window.appendFlash(
            "Fail to update review status. Look at developer console to check the error",
            "danger"
          )
          console.error(data.response.error)
        })
      )
  }

  mapStatusToAction(nextStatus) {
    if (this.state.published && nextStatus > REVIEW_STATUS_PASSED_REVIEW) {
      return {
        action: "unpublish",
        cta: "Unpublish this service",
        phrase: `This service is published.
          You are authorized to unpublish this service.`,
        affirm: `Are you sure you want to unpublish this service?
          Unpublishing a service using this tool should be used in rare cases where a service was unintentionally published.
          A service may only be unpublished if it has no more than 100 users.`,
      }
    } else if (
      this.props.isStaging &&
      nextStatus > REVIEW_STATUS_PASSED_REVIEW
    ) {
      return {
        action: "migrate",
        cta: "Migrate this service",
        phrase: `This staging service has passed review.
          You are authorized to mark this staging service as migrated`,
        affirm: `Are you sure you want to mark this staging service as migrated?
          The developers of this service will receive an email notification that the migration has been completed and the service has been unlocked for further development.`,
      }
    } else {
      return [
        {
          action: "reject",
          cta: "Reject",
          affirm: `Are you sure you want to reject this ${
            this.props.isStaging ? "staging " : ""
          }service?
            This will send an email notification to the developers of this service letting them know that their service has new feedback that needs to be addressed.
            They will be able to edit the service again.`,
        },
        {
          action: "start",
          cta: "Submit for review",
          phrase:
            "This channel is in development and not yet in review. You are authorized to submit this Service for review.",
          affirm: `Are you sure you want to submit this service for review?
            This will send an email notification to the developers of this service, as well as to the IFTTT review team.
            The developer will not be able to make any changes to the service while it is in review.`,
        },
        {
          action: "approve",
          cta: "Approve",
          phrase: `This service is in review.
            You are authorized to approve or reject this service.`,
          alternateAction: "reject",
          affirm: `Are you sure you want to approve this ${
            this.props.isStaging ? "staging " : ""
          }service?
            This will send an email notification to the IFTTT marketing team and the developers of this service letting them know that the service has passed review and is ready to be published.
            The developer will not be able to make any technical changes to the service.`,
        },
        {
          action: "publish",
          cta: "Publish this service",
          phrase: `This service has passed review.
            You are authorized to publish this service.`,
          affirm: `Are you sure you want to publish this service?
            It will be instantly visible to all IFTTT users. Applets that monitor new services on IFTTT will trigger from this event.`,
        },
      ][nextStatus]
    }
  }

  reviewPublishButton() {
    const { reviewStatus, rejectSelected } = this.state

    const pendingAction = this.mapStatusToAction(reviewStatus + 1)
    const rejectAction =
      reviewStatus + 1 === REVIEW_STATUS_PASSED_REVIEW &&
      this.mapStatusToAction(REVIEW_STATUS_IN_DEVELOPMENT)

    return (
      <>
        {this.state.verifyIntent ? (
          <>
            <p className={styles["review-step"]}>
              {rejectSelected ? rejectAction.affirm : pendingAction.affirm}
            </p>
            <button
              className="button-tertiary"
              onClick={
                rejectSelected
                  ? this.returnToDevelopment
                  : this.advanceReviewProgress
              }
              disabled={this.state.updating}
            >
              Yes
            </button>
            <button
              className={classnames("button-tertiary", styles["button-margin"])}
              onClick={this.intentWasVerified}
              disabled={this.state.updating}
            >
              No
            </button>
          </>
        ) : (
          <>
            <p className={styles["review-step"]}>{pendingAction.phrase}</p>
            <button
              className="button-tertiary"
              onClick={() => this.mustVerifyIntent(false)}
            >
              {pendingAction.cta}
            </button>
            {rejectAction && (
              <button
                className={classnames("button-tertiary", styles["button-margin"])}
                onClick={() => this.mustVerifyIntent(true)}
              >
                {rejectAction.cta}
              </button>
            )}
          </>
        )}
        {this.state.updating && <Spinner />}
      </>
    )
  }

  updateCache = url => {
    post(url, { organization_id: this.props.service.organization.id })
      .then(() => {
        window.appendFlash("Cache cleaned")
      })
      .catch(() => {
        window.appendFlash("Error cleaning cache", "danger")
      })
  }

  cacheSection() {
    const { clearOrgUsersCache, clearMyOrgCache, clearServiceListingCaches } = this.props.cachePaths

    return (
      <>
        <h4>Clear cache</h4>
        <p>Clear cache of organizations and services.</p>
        <button
          className="button-tertiary"
          onClick={() => this.updateCache(clearOrgUsersCache)}
          disabled={this.state.updating}
        >
          Clear Organization cache
        </button>
        <button
          className={classnames("button-tertiary", styles["button-margin"])}
          onClick={() => this.updateCache(clearMyOrgCache)}
          disabled={this.state.updating}
        >
          Clear my cache
        </button>
        <button
          className={classnames("button-tertiary", styles["button-margin"])}
          onClick={() => this.updateCache(clearServiceListingCaches)}
          disabled={this.state.updating}
        >
          Clear DIY and search service caches
        </button>
      </>
    )
  }

  openMigrationTool() {
    return (
      <>
        <h4>Migrations</h4>
        <button
          className="button-tertiary"
          onClick={() => {
            window.open(
              `https://ifttt.com/admin/channels/${this.props.service.slug}/migration`,
              "_blank"
            )
          }}
        >
          Open migration tool
        </button>
      </>
    )
  }

  adminLinks() {
    return (
      <>
        <h4>Admin links</h4>
        <button
          className="button-tertiary"
          onClick={() => {
            window.open(
              `https://ifttt.com/admin/channels/${this.props.service.slug}`,
              "_blank"
            )
          }}
        >
          Service page
        </button>
        <button
          className={classnames("button-tertiary", styles["button-margin"])}
          onClick={() => {
            window.open(
              `https://ifttt.com/admin/organizations/${this.props.service.organization.id}`,
              "_blank"
            )
          }}
        >
          Organization page
        </button>
      </>
    )
  }

  openEmailToOrgAdmins() {
    return (
      <>
        <h4>Org admins</h4>
        <button
          className="button-tertiary"
          onClick={() => {
            window.open(
              `mailto:${this.props.service.organization.users
                .map(u => u.email)
                .join()}?bcc=platform-support@ifttt.com`,
              "_blank"
            )
          }}
        >
          Prepare an email to admins
        </button>
      </>
    )
  }

  render() {
    return (
      <HoverTrigger triggerLocation={this.props.triggerLocation}>
        <section className={styles.panel}>
          <div>
            <h2>Admin Service Review</h2>
            {this.reviewPublishButton()}
          </div>
          <div>{this.openMigrationTool()}</div>
          <div>{this.cacheSection()}</div>
          <div>{this.openEmailToOrgAdmins()}</div>
          <div>{this.adminLinks()}</div>
        </section>
      </HoverTrigger>
    )
  }
}

AdminPanel.propTypes = {
  reviewStatus: PropTypes.number,
  published: PropTypes.bool,
  paths: PropTypes.object,
  cachePaths: PropTypes.object,
  service: PropTypes.object,
  isStaging: PropTypes.bool,
  triggerLocation: PropTypes.string.isRequired,
}
